import {
  RxGateReceiptsResponseData,
  RxGateCurrentReceiptData,
  RxGateCurrentReceiptItem,
} from './types';

export const rxGateformatRecipeData = (data: any) => ({
  recipeDate: data.recipe_date,
  tradeName: {
    id: String(data.trade_name?.uuid),
    name: data.trade_name?.trade_name,
    tradeNameUuid: data.medicine_prices_fsms?.map(
      (el: { tradeNameUuid: any }) => el.tradeNameUuid
    ),
  },
  createdDate: data.created_date,
  mnn: {
    id: String(data.mnn?.id),
    name: data.mnn?.name_ru,
  },
  nosology: data.nosology?.name_ru,
  dosageConcentration: data.dosage_concentration,
  category: data.category?.name_ru,
  drugDosageForm: data.drug_dosage_form?.name_ru,
  medServiceType: data.med_service_type?.name_ru,
  quantity: data.quantity,
});

export const formatMedicinesListData = (data: any) => ({
  ...data,
});

// Хелпер для рассчета максимальной скидки относительно суммы товаров и items_cover_percent
// Логика по бонусам пригодится позже, поэтому сразу добавляю хелпер

export const rxGateFormatReceipt = (receipts: RxGateReceiptsResponseData) => {
  if (!receipts?.docks?.length) return receipts;

  const recieptsArr = receipts.docks.map(
    (receipt: RxGateCurrentReceiptData) => {
      if (!receipt?.products?.length) return receipt;

      // 1) Сумма чеков и максимально допустимая скидка (с учетом допустимой скидки аптеки)
      const itemsSum = receipt.products.reduce(
        (acc, item) => acc + (item.price * item.quantity || 0),
        0
      );
      const maxDiscount =
        (itemsSum / 100) * (receipt?.items_cover_percent || 1);

      let remaining = maxDiscount; // // общий неистраченный остаток

      // копия товаров с базовой макс скидкой
      // percentToClient: если с бека приходит скидка по товару юзаем ее иначе используем дефолт скидку по чеку
      let items = receipt.products.map((item) => {
        return {
          ...item,
          maxBonusDiscount: item.maxBonusDiscount || 0,
          percentToClient: item.bonus_percent ?? receipt.items_check_percent,
          percentToPharmacist: receipt.pharmacist_check_percent,
        };
      });

      const shouldNotContinue = (items: RxGateCurrentReceiptItem[]) =>
        items.every(
          (item) =>
            item.maxBonusDiscount >= item.price * item.quantity ||
            item.quantity > remaining
        );

      const updateItems = (items: RxGateCurrentReceiptItem[]) =>
        items.map((item) => {
          // Проверяем что еще можем списать по товару
          if (
            item.quantity <= remaining &&
            item.price >= item.quantity &&
            item.maxBonusDiscount + item.quantity <= item.price * item.quantity
          ) {
            // если можем, увеличиваем макс искаунт и уменьшаем остаток
            remaining -= item.quantity;
            return {
              ...item,
              maxBonusDiscount: item.maxBonusDiscount + item.quantity,
            };
          }
          // если не можем, возвращаем товар как есть
          return item;
        });

      // проходимся по всем товарам и стараемся максимально израсходовать остаток
      while (remaining > 0) {
        items = updateItems(items);

        // смотрим можем ли пройтись еще раз
        if (remaining === 0 || shouldNotContinue(items)) {
          // если не можем, останавливаемся
          break;
        }
      }

      // 4) Возвращаем ту же структуру, но с обновлёнными items
      return {
        ...receipt,
        items,
      };
    }
  );

  return { checks: recieptsArr };
};

// округление вниз до кратности quantity
const floorToMultiple = (value: number, multiple: number) => {
  if (!multiple || multiple <= 0) return Math.floor(value);
  return Math.floor(value / multiple) * multiple;
};

// Хелпер для рассчета максимальной скидки с учетом бонусов клиента
export function rxGateDistributeClientBonus(
  items: RxGateCurrentReceiptItem[],
  clientBonus: number
): RxGateCurrentReceiptItem[] {
  if (!items?.length) return items;

  const bonusesToReceive = (
    it: RxGateCurrentReceiptItem,
    bonusCount: number,
    percent: number
  ) =>
    Math.round(((it.price * it.quantity - bonusCount) / 100) * (percent || 1));

  if (!clientBonus)
    return items.map((it) => ({
      ...it,
      appliedBonus: 0,
      bonusesToClient: bonusesToReceive(it, 0, it.percentToClient),
      bonusesToPharmacist: bonusesToReceive(it, 0, it.percentToPharmacist),
    }));

  const count = items.length;
  const baseShare = clientBonus / count;

  // рабочая копия
  const work = items.map((item) => ({
    ...item,
    appliedWork: 0,
    capByItem: Math.min(
      item.maxBonusDiscount != null
        ? item.maxBonusDiscount
        : item.price * item.quantity,
      item.price * item.quantity
    ),
  }));

  let remaining = clientBonus;
  let carry = 0;

  // === Первый проход: равные доли с округлением до кратности quantity ===
  for (let i = 0; i < work.length; i += 1) {
    const w = work[i];
    const q = Math.max(1, w.quantity || 1);

    const allowedShare = baseShare + carry;
    const rawAlloc = Math.min(allowedShare, w.capByItem, remaining);

    // округляем до кратности quantity, чтобы на единицу было целое число бонусов
    const alloc = floorToMultiple(rawAlloc, q);

    w.appliedWork = alloc;
    remaining -= alloc;
    carry = Math.max(0, allowedShare - alloc);
  }

  // === Второй проход: «докрутка» остатка пакетами кратными quantity ===
  if (remaining > 0) {
    // сортируем по возрастанию кратности для большей эффективности
    const orderIdx = [...Array(work.length).keys()].sort(
      (a, b) => (work[a].quantity || 1) - (work[b].quantity || 1)
    );

    let progress = true;
    while (remaining > 0 && progress) {
      progress = false;

      for (let k = 0; k < orderIdx.length; k += 1) {
        const idx = orderIdx[k];
        const w = work[idx];
        const q = Math.max(1, w.quantity || 1);

        // проверяем, хватает ли остатка и лимита хотя бы на один «пакет»
        if (remaining >= q) {
          const room = w.capByItem - w.appliedWork;
          if (room >= q) {
            // применяем ровно один пакет
            w.appliedWork += q;
            remaining -= q;
            progress = true;

            if (remaining <= 0) break;
          }
        }
      }
    }
  }

  return work.map((w) => {
    const appliedBonus = Math.floor(w.appliedWork);

    return {
      ...w,
      appliedBonus,
      bonusesToClient: bonusesToReceive(w, appliedBonus, w.percentToClient),
      bonusesToPharmacist: bonusesToReceive(
        w,
        appliedBonus,
        w.percentToPharmacist
      ),
    } as RxGateCurrentReceiptItem;
  });
}

export const formatDataForLoyaltyReport = (
  startDay: Date,
  endDay: Date,
  integrationCode: string,
  source?: string,
  phone?: string
) => {
  const getDateString = (st: Date) => {
    const formatedStr = st
      .toLocaleString()
      .replace(/[a-zа-яё]/gi, '')
      .trim();
    const ar = formatedStr.split(', ');

    const month =
      st.getMonth() + 1 > 9 ? st.getMonth() + 1 : `0${st.getMonth() + 1}`;
    const day = st.getDate() > 9 ? st.getDate() : `0${st.getDate()}`;
    const year = st.getFullYear();
    const getGMT = st.toString().split('GMT')[1].split(' ')[0].slice(1, 3);

    return getGMT === '00'
      ? `${year}-${month}-${day}T${ar[1]}Z`
      : `${year}-${month}-${day}T${ar[1]}+${getGMT}:00`;
  };

  const sourceCode = source ? { source_code: source } : {};
  const phoneCode = phone ? { phone } : {};

  return {
    from: getDateString(startDay),
    to: getDateString(endDay),
    type: 'pharmacy',
    integration_code: integrationCode,
    ...sourceCode,
    ...phoneCode,
  };
};
